import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient'; // Your configured Supabase client
import type { WalletRelease as OriginalWalletRelease } from '../api/github/types'; // Original type structure
import { FALLBACK_RELEASE } from '../api/github/constants'; // Fallback data

// Constants for refetch logic (can be adjusted)
const REFETCH_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes for release info
const MAX_DATA_AGE_MS = 30 * 60 * 1000;   // Refetch if data is older than 30 minutes

// Interface for data coming from Supabase (snake_case)
// This should match the columns in your 'wallet_latest_release' table
interface DbWalletLatestRelease {
  id?: number;
  version: string | null;
  published_at: string | null; // ISO 8601 string
  assets_windows_x32_url: string | null;
  assets_windows_x64_url: string | null;
  assets_mac_arm64_url: string | null;
  assets_mac_x86_64_url: string | null;
  assets_linux_aarch64_url: string | null;
  assets_linux_arm_url: string | null;
  assets_linux_x86_url: string | null;
  assets_linux_x64_url: string | null;
  last_updated_at?: string | null;
}

// Use the existing camelCase WalletRelease type for the context value
interface ReleaseContextType {
  release: OriginalWalletRelease | null;
  loading: boolean;
  error: Error | null;
}

const ReleaseContext = createContext<ReleaseContextType | undefined>(undefined);

// Helper to transform DB data to the OriginalWalletRelease format
const transformDbDataToWalletRelease = (dbData: DbWalletLatestRelease | null): OriginalWalletRelease | null => {
  if (!dbData || !dbData.version || !dbData.published_at) { // Basic check for essential data
    return FALLBACK_RELEASE; // Use fallback if essential data is missing
  }
  return {
    version: dbData.version,
    publishedAt: dbData.published_at,
    assets: {
      windows: {
        x32: dbData.assets_windows_x32_url || undefined, // Ensure undefined if null for type compatibility
        x64: dbData.assets_windows_x64_url || undefined,
      },
      mac: {
        arm64: dbData.assets_mac_arm64_url || undefined,
        x86_64: dbData.assets_mac_x86_64_url || undefined,
      },
      linux: {
        aarch64: dbData.assets_linux_aarch64_url || undefined,
        arm: dbData.assets_linux_arm_url || undefined,
        x86: dbData.assets_linux_x86_url || undefined,
        x64: dbData.assets_linux_x64_url || undefined,
      },
    },
  };
};

export function ReleaseProvider({ children }: { children: ReactNode }) {
  const [release, setRelease] = useState<OriginalWalletRelease | null>(() => transformDbDataToWalletRelease(null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTimestamp, setLastFetchTimestamp] = useState<number>(0);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (!isInitialLoad) {
      // No loading indicator for background refetches
    }
    setError(null);
    try {
      const { data: dbData, error: dbError } = await supabase
        .from('wallet_latest_release')
        .select('*')
        .eq('id', 1)
        .single<DbWalletLatestRelease>();

      if (dbError) {
        console.error('Error fetching wallet release from Supabase:', dbError);
        if (isInitialLoad) setRelease(FALLBACK_RELEASE);
        setError(new Error('Failed to fetch wallet release: ' + dbError.message));
      } else if (dbData) {
        setRelease(transformDbDataToWalletRelease(dbData));
        setLastFetchTimestamp(Date.now());
      } else {
        if (isInitialLoad) setRelease(FALLBACK_RELEASE); 
        setError(new Error('No wallet release data found in database.'));
      }
    } catch (err) {
      console.error('Catch block error fetching wallet release:', err);
      if (isInitialLoad) setRelease(FALLBACK_RELEASE);
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    fetchData(true);
  }, [fetchData]);

  // Periodic refetch if data is stale and tab is visible
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[ReleaseContext] Data is stale, refetching due to interval...');
          fetchData();
        }
      }
    }, REFETCH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [lastFetchTimestamp, fetchData]);

  // Refetch on window focus if data is stale
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[ReleaseContext] Tab focused and data is stale, refetching...');
          fetchData();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastFetchTimestamp, fetchData]);

  return (
    <ReleaseContext.Provider value={{ release, loading, error }}>
      {children}
    </ReleaseContext.Provider>
  );
}

export function useRelease() {
  const context = useContext(ReleaseContext);
  if (context === undefined) {
    throw new Error('useRelease must be used within a ReleaseProvider');
  }
  return context;
}